import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { clearLocalStateByPrefix } from '../hooks/useLocalState';

const API_URL = process.env.REACT_APP_API_URL || '';
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const apiFetch = useCallback(async (path, options = {}) => {
    const res = await fetch(`${API_URL}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        // CSRF: カスタムヘッダ方式 — フォーム CSRF を弾く + CORS preflight で攻撃ドメインから到達不可
        'X-Requested-With': 'penlytics',
        ...options.headers,
      },
    });
    return res;
  }, []);

  const loadUser = useCallback(async () => {
    try {
      const res = await apiFetch('/api/auth/me');
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    const res = await apiFetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      // ★ 2026-05-14: res.json() が HTML エラーページ等で SyntaxError を throw すると
      //   "Unexpected token <" 等の技術的 message が UI に出ていた。 安全に message 抽出。
      const d = await res.json().catch(() => ({}));
      const msg = d?.message || (res.status === 429
        ? 'ログイン試行が多すぎます。 少し時間をおいて再度お試しください。'
        : res.status >= 500
          ? 'サーバーで一時的な問題が発生しています。 しばらくしてから再度お試しください。'
          : 'ログインに失敗しました。 メールアドレスとパスワードをご確認ください。');
      const err = new Error(msg);
      err.code = d?.code;
      err.status = res.status;
      throw err;
    }
    const data = await res.json();
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' });
    } catch { /* ignore */ }
    // ★ 2026-05-15 iter 27 致命修正: cross-user 漏洩防止 — 共用 PC で前ユーザーの freeInput / customInstructions
    //   / foreshadowing overrides 等が localStorage に 30 日残るのを logout で即削除。
    try {
      clearLocalStateByPrefix('freeinput:');
      clearLocalStateByPrefix('qa:');
    } catch (e) { /* clear が失敗しても logout は続行 */ }
    setUser(null);
  };

  const refreshUser = async () => {
    await loadUser();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, apiFetch }}>
      {children}
    </AuthContext.Provider>
  );
};
